import { createSlice } from '@reduxjs/toolkit';

import {
  fetchOrderAttachments,
  fetchOrderOrgs,
  fetchOrders,
  fetchOrderSet,
  fetchOrderSetShippingOptions,
  fetchUserRecommendations,
} from './orderHistoryActions';

import { listenerMiddleware } from '../redux/middleware';
import type OrderHistoryTypes from './orderHistoryTypes';

export type OrderHistoryState = {
  attachments: OrderHistoryTypes.OrderAttachmentMap,
  last: boolean | null,
  orders: OrderHistoryTypes.OrderDetail[],
  orderSets: Record<OrderHistoryTypes.OrderSetId, OrderHistoryTypes.OrderSet>,
  orderSetShippingOptions: Record<OrderHistoryTypes.OrderSetId, OrderHistoryTypes.ChangeShippingAddressResponse>,
  orgs: OrderHistoryTypes.Organization[],
  page: number,
  pendingFetchCount: number,
  /** { [orgId]: { [productCode]: true } } */
  userRecs: Record<number, Record<string, boolean>>,
};

const initialState: OrderHistoryState = {
  attachments: {},
  last: null,
  orders: [],
  orderSets: {},
  orderSetShippingOptions: {},
  orgs: [],
  page: 0,
  pendingFetchCount: 0,
  userRecs: {},
};

const orderHistoryReducer = createSlice({
  name: 'orderHistory',
  initialState,
  reducers: {
    decrementPendingFetchCount(state) {
      state.pendingFetchCount -= 1;
    },
    incrementPendingFetchCount(state) {
      state.pendingFetchCount += 1;
    },
    markFFLOrders(state) {
      state.orders = state.orders.map((order) => {
        if (order.groups.some((group) => group.lines.some((line) => line.options.META_REQUIRES_FFL))) {
          return { ...order, isFFL: true };
        }

        return order;
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOrderAttachments.fulfilled, (state, action) => {
      state.attachments = action.payload.data;
    });

    builder.addCase(fetchOrderOrgs.fulfilled, (state, action) => {
      state.orgs = action.payload.data;
    });

    builder.addCase(fetchOrderOrgs.rejected, (state) => {
      state.orgs = [];
    });

    builder.addCase(fetchOrders.fulfilled, (state, action) => {
      const updatedOrders =
        action.payload.data.number > 0
          ? state.orders.concat(action.payload.data.content)
          : action.payload.data.content;

      state.orders = updatedOrders;

      state.last = action.payload.data.last;
      state.page = action.payload.data.number;
    });

    builder.addCase(fetchOrderSet.fulfilled, (state, action) => {
      if (action.payload.data) {
        const orderSet = action.payload.data;

        state.orderSets[orderSet.orderSetId] = orderSet;

        const orderSetOrders = new Map(orderSet.orders.map((order) => [order.id, order]));

        // update existing orders with fresh data from the order set
        state.orders = state.orders.map((order) => orderSetOrders.get(order.id) || order);
      }
    });

    builder.addCase(fetchOrderSetShippingOptions.fulfilled, (state, action) => {
      if (action.payload.data) {
        const orderSetShippingOptions = action.payload.data;

        state.orderSetShippingOptions[orderSetShippingOptions.orderSetId] = orderSetShippingOptions;
      }
    });

    builder.addCase(fetchUserRecommendations.fulfilled, (state, action) => {
      if (action.payload.data?.results) {
        const userRecs = action.payload.data.results?.reduce((acc, recommendation) => {
          if (recommendation?.reviewedEntity) {
            const entity = recommendation.reviewedEntity;
            const recs = window.structuredClone(acc);

            if (!recs[entity.ownerId]) {
              recs[entity.ownerId] = {};
            }

            recs[entity.ownerId][entity.id] = true;

            return recs;
          }

          return acc;
        }, {}) || {};

        state.userRecs = userRecs;
      }
    });
  },
});

listenerMiddleware.startListening({
  predicate: (action) => action.type.startsWith('orderHistory/') && action.type.endsWith('/pending'),
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(orderHistoryReducer.actions.incrementPendingFetchCount());
  },
});

listenerMiddleware.startListening({
  predicate: (action) => action.type.startsWith('orderHistory/') && (action.type.endsWith('/rejected') || action.type.endsWith('/fulfilled')),
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(orderHistoryReducer.actions.decrementPendingFetchCount());
  },
});

listenerMiddleware.startListening({
  predicate: (action) => [fetchOrders.fulfilled.type, fetchOrderSet.fulfilled.type].includes(action.type),
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(orderHistoryReducer.actions.markFFLOrders());
  },
});

export default orderHistoryReducer.reducer;
